import { useState, useEffect } from "react";
import { FiUpload, FiX, FiCheck, FiLoader, FiTrash2 } from "react-icons/fi";
import { adminFetch, apiEndpoints, API_BASE_URL } from "../config/api";

const ManageTaxi = () => {
  const [formData, setFormData] = useState({
    vehicleType: "Car",
    vehicleName: "",
    description: "",
    capacity: {
      passengers: 7,
      luggage: 4,
    },
    pricing: {
      basePrice: 50,
      pricePerKm: 2.5,
      airportTransfer: 75,
      fullDayRate: 300,
      currency: "USD",
    },
    features: [],
    images: [],
    driverInfo: {
      languagesSpoken: [],
      experience: "",
    },
    serviceAreas: [],
    isAvailable: true,
    isActive: true,
  });

  const [featureInput, setFeatureInput] = useState("");
  const [languageInput, setLanguageInput] = useState("");
  const [serviceAreaInput, setServiceAreaInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Image upload states
  const [imageUploadFile, setImageUploadFile] = useState(null);
  const [imageUploadTitle, setImageUploadTitle] = useState("");
  const [imageUploadCategory] = useState("taxi");
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadStatus, setImageUploadStatus] = useState("");

  // Taxis list states
  const [taxis, setTaxis] = useState([]);
  const [loadingTaxis, setLoadingTaxis] = useState(false);
  const [deletingTaxi, setDeletingTaxi] = useState(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === "number" ? parseFloat(value) || 0 : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleCapacityChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      capacity: {
        ...prev.capacity,
        [field]: parseInt(value) || 0,
      },
    }));
  };

  const handlePricingChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [field]: parseFloat(value) || 0,
      },
    }));
  };

  const handleAddFeature = () => {
    if (featureInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, featureInput.trim()],
      }));
      setFeatureInput("");
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleAddLanguage = () => {
    if (languageInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        driverInfo: {
          ...prev.driverInfo,
          languagesSpoken: [...prev.driverInfo.languagesSpoken, languageInput.trim()],
        },
      }));
      setLanguageInput("");
    }
  };

  const handleRemoveLanguage = (index) => {
    setFormData((prev) => ({
      ...prev,
      driverInfo: {
        ...prev.driverInfo,
        languagesSpoken: prev.driverInfo.languagesSpoken.filter((_, i) => i !== index),
      },
    }));
  };

  const handleAddServiceArea = () => {
    if (serviceAreaInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        serviceAreas: [...prev.serviceAreas, serviceAreaInput.trim()],
      }));
      setServiceAreaInput("");
    }
  };

  const handleRemoveServiceArea = (index) => {
    setFormData((prev) => ({
      ...prev,
      serviceAreas: prev.serviceAreas.filter((_, i) => i !== index),
    }));
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    if (!imageUploadFile || !imageUploadTitle.trim()) {
      setImageUploadStatus("Please select an image and provide a title");
      return;
    }

    setImageUploading(true);
    setImageUploadStatus("");

    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", imageUploadFile);
      uploadFormData.append("category", imageUploadCategory);
      uploadFormData.append("title", imageUploadTitle.trim());

      const response = await adminFetch(apiEndpoints.images.base, {
        method: "POST",
        body: uploadFormData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Image upload failed");
      }

      const returnedUrl = result.url || (result.data && result.data.url);
      const urlToUse = returnedUrl
        ? returnedUrl.startsWith("http")
          ? returnedUrl
          : apiEndpoints.images.url(returnedUrl)
        : "";

      if (!urlToUse) {
        throw new Error("Upload succeeded but no URL was returned");
      }

      // Add image to form data
      const isFirstImage = formData.images.length === 0;
      const newImage = {
        url: urlToUse,
        caption: imageUploadTitle.trim(),
        isFeatured: isFirstImage,
      };

      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, newImage],
      }));

      setImageUploadStatus("Image uploaded successfully!");
      setImageUploadFile(null);
      setImageUploadTitle("");
      setTimeout(() => setImageUploadStatus(""), 3000);
    } catch (err) {
      console.error("Error uploading image:", err);
      setImageUploadStatus(err.message || "Failed to upload image");
    } finally {
      setImageUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleSetFeaturedImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.map((img, i) => ({
        ...img,
        isFeatured: i === index,
      })),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess(false);

    try {
      // Validate required fields
      if (!formData.vehicleName.trim()) {
        throw new Error("Vehicle name is required");
      }
      if (!formData.description.trim()) {
        throw new Error("Description is required");
      }
      if (formData.images.length === 0) {
        throw new Error("At least one image is required");
      }

      const response = await adminFetch(apiEndpoints.taxis.base, {
        method: "POST",
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to create taxi");
      }

      setSuccess(true);
      setError("");

      // Reset form
      setFormData({
        vehicleType: "Jeep",
        vehicleName: "",
        description: "",
        capacity: {
          passengers: 7,
          luggage: 4,
        },
        pricing: {
          basePrice: 50,
          pricePerKm: 2.5,
          airportTransfer: 75,
          fullDayRate: 300,
          currency: "USD",
        },
        features: [],
        images: [],
        driverInfo: {
          languagesSpoken: [],
          experience: "",
        },
        serviceAreas: [],
        isAvailable: true,
        isActive: true,
      });

      setTimeout(() => setSuccess(false), 5000);
      
      // Refresh taxis list after successful creation
      fetchTaxis();
    } catch (err) {
      console.error("Error creating taxi:", err);
      setError(err.message || "Failed to create taxi");
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch all taxis
  const fetchTaxis = async () => {
    try {
      setLoadingTaxis(true);
      const response = await adminFetch(apiEndpoints.taxis.base);
      const data = await response.json();

      if (data.success && data.data && Array.isArray(data.data)) {
        setTaxis(data.data);
      } else {
        setTaxis([]);
      }
    } catch (err) {
      console.error("Error fetching taxis:", err);
      setTaxis([]);
    } finally {
      setLoadingTaxis(false);
    }
  };

  // Delete taxi
  const handleDeleteTaxi = async (id) => {
    if (!window.confirm("Are you sure you want to delete this taxi? This action cannot be undone.")) {
      return;
    }

    try {
      setDeletingTaxi(id);
      const response = await adminFetch(`${apiEndpoints.taxis.base}/${id}`, {
        method: "DELETE",
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete taxi");
      }

      // Refresh taxis list after successful deletion
      fetchTaxis();
    } catch (err) {
      console.error("Error deleting taxi:", err);
      alert(err.message || "Failed to delete taxi");
    } finally {
      setDeletingTaxi(null);
    }
  };

  // Fetch taxis on component mount
  useEffect(() => {
    fetchTaxis();
  }, []);

  return (
    <div style={{ padding: "2rem", maxWidth: "1200px", margin: "0 auto" }}>
      <div style={{ marginBottom: "2rem" }}>
        <h2 style={{ fontSize: "1.75rem", fontWeight: 700, color: "#ffffff", marginBottom: "0.5rem" }}>
          Add New Taxi
        </h2>
        <p style={{ color: "rgba(255, 255, 255, 0.6)", fontSize: "0.9rem" }}>
          Create a new taxi listing with vehicle details, pricing, and images
        </p>
      </div>

      {success && (
        <div
          style={{
            padding: "1rem",
            marginBottom: "1.5rem",
            backgroundColor: "rgba(34, 197, 94, 0.2)",
            border: "1px solid rgba(34, 197, 94, 0.4)",
            borderRadius: "0.5rem",
            color: "#22c55e",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <FiCheck />
          <span>Taxi created successfully!</span>
        </div>
      )}

      {error && (
        <div
          style={{
            padding: "1rem",
            marginBottom: "1.5rem",
            backgroundColor: "rgba(239, 68, 68, 0.2)",
            border: "1px solid rgba(239, 68, 68, 0.4)",
            borderRadius: "0.5rem",
            color: "#ef4444",
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
        {/* Basic Information */}
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "1rem",
            padding: "1.5rem",
          }}
        >
          <h3 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#ffffff", marginBottom: "1.5rem" }}>
            Basic Information
          </h3>

          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", color: "rgba(255, 255, 255, 0.8)", fontSize: "0.9rem" }}>
                Vehicle Type *
              </label>
              <select
                name="vehicleType"
                value={formData.vehicleType}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  borderRadius: "0.5rem",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#ffffff",
                  fontSize: "0.9rem",
                }}
              >
           
                <option value="Car" style={{ color: "#000000" }}>Car</option>
                <option value="Van" style={{ color: "#000000" }}>Van</option>
                <option value="Bus" style={{ color: "#000000" }}>Bus</option>
              </select>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", color: "rgba(255, 255, 255, 0.8)", fontSize: "0.9rem" }}>
                Vehicle Name *
              </label>
              <input
                type="text"
                name="vehicleName"
                value={formData.vehicleName}
                onChange={handleInputChange}
                required
                placeholder="e.g., Toyota Land Cruiser 2023"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  borderRadius: "0.5rem",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#ffffff",
                  fontSize: "0.9rem",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", color: "rgba(255, 255, 255, 0.8)", fontSize: "0.9rem" }}>
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={4}
                placeholder="Describe the vehicle, its features, and what makes it special..."
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  borderRadius: "0.5rem",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#ffffff",
                  fontSize: "0.9rem",
                  resize: "vertical",
                }}
              />
            </div>
          </div>
        </div>

        {/* Capacity */}
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "1rem",
            padding: "1.5rem",
          }}
        >
          <h3 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#ffffff", marginBottom: "1.5rem" }}>
            Capacity
          </h3>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", color: "rgba(255, 255, 255, 0.8)", fontSize: "0.9rem" }}>
                Passengers
              </label>
              <input
                type="number"
                value={formData.capacity.passengers}
                onChange={(e) => handleCapacityChange("passengers", e.target.value)}
                min="1"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  borderRadius: "0.5rem",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#ffffff",
                  fontSize: "0.9rem",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", color: "rgba(255, 255, 255, 0.8)", fontSize: "0.9rem" }}>
                Luggage
              </label>
              <input
                type="number"
                value={formData.capacity.luggage}
                onChange={(e) => handleCapacityChange("luggage", e.target.value)}
                min="0"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  borderRadius: "0.5rem",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#ffffff",
                  fontSize: "0.9rem",
                }}
              />
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "1rem",
            padding: "1.5rem",
          }}
        >
          <h3 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#ffffff", marginBottom: "1.5rem" }}>
            Pricing
          </h3>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "1rem" }}>
            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", color: "rgba(255, 255, 255, 0.8)", fontSize: "0.9rem" }}>
                Base Price ({formData.pricing.currency})
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.pricing.basePrice}
                onChange={(e) => handlePricingChange("basePrice", e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  borderRadius: "0.5rem",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#ffffff",
                  fontSize: "0.9rem",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", color: "rgba(255, 255, 255, 0.8)", fontSize: "0.9rem" }}>
                Price Per Km ({formData.pricing.currency})
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.pricing.pricePerKm}
                onChange={(e) => handlePricingChange("pricePerKm", e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  borderRadius: "0.5rem",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#ffffff",
                  fontSize: "0.9rem",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", color: "rgba(255, 255, 255, 0.8)", fontSize: "0.9rem" }}>
                Airport Transfer ({formData.pricing.currency})
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.pricing.airportTransfer}
                onChange={(e) => handlePricingChange("airportTransfer", e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  borderRadius: "0.5rem",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#ffffff",
                  fontSize: "0.9rem",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", color: "rgba(255, 255, 255, 0.8)", fontSize: "0.9rem" }}>
                Full Day Rate ({formData.pricing.currency})
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.pricing.fullDayRate}
                onChange={(e) => handlePricingChange("fullDayRate", e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  borderRadius: "0.5rem",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#ffffff",
                  fontSize: "0.9rem",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", color: "rgba(255, 255, 255, 0.8)", fontSize: "0.9rem" }}>
                Currency
              </label>
              <select
                value={formData.pricing.currency}
                onChange={(e) => handlePricingChange("currency", e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  borderRadius: "0.5rem",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#ffffff",
                  fontSize: "0.9rem",
                }}
              >
                <option value="USD" style={{ color: "#000000" }}>USD</option>
                <option value="LKR" style={{ color: "#000000" }}>LKR</option>
                <option value="EUR" style={{ color: "#000000" }}>EUR</option>
              </select>
            </div>
          </div>
        </div>

        {/* Features */}
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "1rem",
            padding: "1.5rem",
          }}
        >
          <h3 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#ffffff", marginBottom: "1.5rem" }}>
            Features
          </h3>

          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
            <input
              type="text"
              value={featureInput}
              onChange={(e) => setFeatureInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddFeature())}
              placeholder="Add a feature (e.g., Air Conditioning)"
              style={{
                flex: 1,
                padding: "0.75rem",
                borderRadius: "0.5rem",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "#ffffff",
                fontSize: "0.9rem",
              }}
            />
            <button
              type="button"
              onClick={handleAddFeature}
              style={{
                padding: "0.75rem 1.5rem",
                borderRadius: "0.5rem",
                border: "none",
                backgroundColor: "#c4915f",
                color: "#ffffff",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Add
            </button>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {formData.features.map((feature, index) => (
              <span
                key={index}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.5rem 1rem",
                  backgroundColor: "rgba(196, 145, 95, 0.2)",
                  border: "1px solid rgba(196, 145, 95, 0.4)",
                  borderRadius: "0.5rem",
                  color: "#c4915f",
                  fontSize: "0.85rem",
                }}
              >
                {feature}
                <button
                  type="button"
                  onClick={() => handleRemoveFeature(index)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#c4915f",
                    cursor: "pointer",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FiX size={16} />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Images */}
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "1rem",
            padding: "1.5rem",
          }}
        >
          <h3 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#ffffff", marginBottom: "1.5rem" }}>
            Images *
          </h3>

          {/* Image Upload Section */}
          <div
            style={{
              padding: "1rem",
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              borderRadius: "0.5rem",
              marginBottom: "1.5rem",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              <div>
                <label style={{ display: "block", marginBottom: "0.5rem", color: "rgba(255, 255, 255, 0.8)", fontSize: "0.9rem" }}>
                  Image File
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImageUploadFile(e.target.files[0])}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    borderRadius: "0.5rem",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "#ffffff",
                    fontSize: "0.9rem",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "0.5rem", color: "rgba(255, 255, 255, 0.8)", fontSize: "0.9rem" }}>
                  Image Title/Caption
                </label>
                <input
                  type="text"
                  value={imageUploadTitle}
                  onChange={(e) => setImageUploadTitle(e.target.value)}
                  placeholder="e.g., Exterior view"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    borderRadius: "0.5rem",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "#ffffff",
                    fontSize: "0.9rem",
                  }}
                />
              </div>

              <button
                type="button"
                onClick={handleImageUpload}
                disabled={imageUploading}
                style={{
                  padding: "0.75rem 1.5rem",
                  borderRadius: "0.5rem",
                  border: "none",
                  backgroundColor: imageUploading ? "rgba(196, 145, 95, 0.5)" : "#c4915f",
                  color: "#ffffff",
                  fontWeight: 600,
                  cursor: imageUploading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  justifyContent: "center",
                }}
              >
                {imageUploading ? (
                  <>
                    <FiLoader style={{ animation: "spin 1s linear infinite" }} />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FiUpload />
                    Upload Image
                  </>
                )}
              </button>

              {imageUploadStatus && (
                <p
                  style={{
                    color: imageUploadStatus.includes("success") ? "#22c55e" : "#ef4444",
                    fontSize: "0.85rem",
                    marginTop: "0.5rem",
                  }}
                >
                  {imageUploadStatus}
                </p>
              )}
            </div>
          </div>

          {/* Uploaded Images */}
          {formData.images.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1rem" }}>
              {formData.images.map((image, index) => (
                <div
                  key={index}
                  style={{
                    position: "relative",
                    borderRadius: "0.5rem",
                    overflow: "hidden",
                    border: image.isFeatured ? "2px solid #c4915f" : "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <img
                    src={image.url}
                    alt={image.caption}
                    style={{
                      width: "100%",
                      height: "150px",
                      objectFit: "cover",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "0.5rem",
                      right: "0.5rem",
                      display: "flex",
                      gap: "0.25rem",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleSetFeaturedImage(index)}
                      style={{
                        padding: "0.25rem 0.5rem",
                        borderRadius: "0.25rem",
                        border: "none",
                        backgroundColor: image.isFeatured ? "#c4915f" : "rgba(0, 0, 0, 0.6)",
                        color: "#ffffff",
                        fontSize: "0.75rem",
                        cursor: "pointer",
                      }}
                    >
                      {image.isFeatured ? "Featured" : "Set Featured"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      style={{
                        padding: "0.25rem",
                        borderRadius: "0.25rem",
                        border: "none",
                        backgroundColor: "rgba(239, 68, 68, 0.8)",
                        color: "#ffffff",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <FiX size={16} />
                    </button>
                  </div>
                  <div
                    style={{
                      padding: "0.5rem",
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      color: "#ffffff",
                      fontSize: "0.85rem",
                    }}
                  >
                    {image.caption}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Driver Info */}
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "1rem",
            padding: "1.5rem",
          }}
        >
          <h3 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#ffffff", marginBottom: "1.5rem" }}>
            Driver Information
          </h3>

          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", color: "rgba(255, 255, 255, 0.8)", fontSize: "0.9rem" }}>
                Languages Spoken
              </label>
              <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
                <input
                  type="text"
                  value={languageInput}
                  onChange={(e) => setLanguageInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddLanguage())}
                  placeholder="e.g., English"
                  style={{
                    flex: 1,
                    padding: "0.75rem",
                    borderRadius: "0.5rem",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "#ffffff",
                    fontSize: "0.9rem",
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddLanguage}
                  style={{
                    padding: "0.75rem 1.5rem",
                    borderRadius: "0.5rem",
                    border: "none",
                    backgroundColor: "#c4915f",
                    color: "#ffffff",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Add
                </button>
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {formData.driverInfo.languagesSpoken.map((lang, index) => (
                  <span
                    key={index}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.5rem 1rem",
                      backgroundColor: "rgba(196, 145, 95, 0.2)",
                      border: "1px solid rgba(196, 145, 95, 0.4)",
                      borderRadius: "0.5rem",
                      color: "#c4915f",
                      fontSize: "0.85rem",
                    }}
                  >
                    {lang}
                    <button
                      type="button"
                      onClick={() => handleRemoveLanguage(index)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#c4915f",
                        cursor: "pointer",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <FiX size={16} />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", color: "rgba(255, 255, 255, 0.8)", fontSize: "0.9rem" }}>
                Experience
              </label>
              <textarea
                value={formData.driverInfo.experience}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    driverInfo: { ...prev.driverInfo, experience: e.target.value },
                  }))
                }
                rows={3}
                placeholder="e.g., 10+ years of professional driving experience"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  borderRadius: "0.5rem",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#ffffff",
                  fontSize: "0.9rem",
                  resize: "vertical",
                }}
              />
            </div>
          </div>
        </div>

        {/* Service Areas */}
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "1rem",
            padding: "1.5rem",
          }}
        >
          <h3 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#ffffff", marginBottom: "1.5rem" }}>
            Service Areas
          </h3>

          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
            <input
              type="text"
              value={serviceAreaInput}
              onChange={(e) => setServiceAreaInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddServiceArea())}
              placeholder="e.g., Colombo"
              style={{
                flex: 1,
                padding: "0.75rem",
                borderRadius: "0.5rem",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "#ffffff",
                fontSize: "0.9rem",
              }}
            />
            <button
              type="button"
              onClick={handleAddServiceArea}
              style={{
                padding: "0.75rem 1.5rem",
                borderRadius: "0.5rem",
                border: "none",
                backgroundColor: "#c4915f",
                color: "#ffffff",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Add
            </button>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {formData.serviceAreas.map((area, index) => (
              <span
                key={index}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.5rem 1rem",
                  backgroundColor: "rgba(196, 145, 95, 0.2)",
                  border: "1px solid rgba(196, 145, 95, 0.4)",
                  borderRadius: "0.5rem",
                  color: "#c4915f",
                  fontSize: "0.85rem",
                }}
              >
                {area}
                <button
                  type="button"
                  onClick={() => handleRemoveServiceArea(index)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#c4915f",
                    cursor: "pointer",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FiX size={16} />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Status */}
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "1rem",
            padding: "1.5rem",
          }}
        >
          <h3 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#ffffff", marginBottom: "1.5rem" }}>
            Status
          </h3>

          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                name="isAvailable"
                checked={formData.isAvailable}
                onChange={handleInputChange}
                style={{
                  width: "20px",
                  height: "20px",
                  cursor: "pointer",
                }}
              />
              <span style={{ color: "rgba(255, 255, 255, 0.8)", fontSize: "0.9rem" }}>Available for booking</span>
            </label>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                style={{
                  width: "20px",
                  height: "20px",
                  cursor: "pointer",
                }}
              />
              <span style={{ color: "rgba(255, 255, 255, 0.8)", fontSize: "0.9rem" }}>Active (visible to customers)</span>
            </label>
          </div>
        </div>

        {/* Submit Button */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
          <button
            type="submit"
            disabled={submitting}
            style={{
              padding: "0.75rem 2rem",
              borderRadius: "0.5rem",
              border: "none",
              backgroundColor: submitting ? "rgba(196, 145, 95, 0.5)" : "#c4915f",
              color: "#ffffff",
              fontWeight: 600,
              fontSize: "1rem",
              cursor: submitting ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            {submitting ? (
              <>
                <FiLoader style={{ animation: "spin 1s linear infinite" }} />
                Creating...
              </>
            ) : (
              <>
                <FiCheck />
                Create Taxi
              </>
            )}
          </button>
        </div>
      </form>

      {/* Available Taxis Section */}
      <div style={{ marginTop: "4rem", paddingTop: "2rem", borderTop: "1px solid rgba(255, 255, 255, 0.1)" }}>
        <div style={{ marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.75rem", fontWeight: 700, color: "#ffffff", marginBottom: "0.5rem" }}>
            Available Taxis
          </h2>
          <p style={{ color: "rgba(255, 255, 255, 0.6)", fontSize: "0.9rem" }}>
            Manage existing taxi listings
          </p>
        </div>

        {loadingTaxis ? (
          <div style={{ textAlign: "center", padding: "2rem", color: "rgba(255, 255, 255, 0.6)" }}>
            <FiLoader style={{ animation: "spin 1s linear infinite", fontSize: "2rem", marginBottom: "1rem" }} />
            <p>Loading taxis...</p>
          </div>
        ) : taxis.length === 0 ? (
          <div
            style={{
              padding: "2rem",
              textAlign: "center",
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "1rem",
              color: "rgba(255, 255, 255, 0.6)",
            }}
          >
            No taxis found. Create your first taxi above.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1.5rem" }}>
            {taxis.map((taxi) => {
              const featuredImage = taxi.images?.find((img) => img.isFeatured) || taxi.images?.[0];
              let imageUrl = "https://images.unsplash.com/photo-1541899481282-d53bffe3c35d?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80";
              if (featuredImage?.url) {
                imageUrl = featuredImage.url.startsWith("http")
                  ? featuredImage.url
                  : `${API_BASE_URL}${featuredImage.url}`;
              }

              return (
                <div
                  key={taxi._id}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "1rem",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div style={{ position: "relative", width: "100%", height: "200px", overflow: "hidden" }}>
                    <img
                      src={imageUrl}
                      alt={taxi.vehicleName}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: "0.5rem",
                        right: "0.5rem",
                        display: "flex",
                        gap: "0.5rem",
                      }}
                    >
                      {!taxi.isActive && (
                        <span
                          style={{
                            padding: "0.25rem 0.75rem",
                            backgroundColor: "rgba(239, 68, 68, 0.8)",
                            color: "#ffffff",
                            borderRadius: "0.25rem",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                          }}
                        >
                          Inactive
                        </span>
                      )}
                      {!taxi.isAvailable && (
                        <span
                          style={{
                            padding: "0.25rem 0.75rem",
                            backgroundColor: "rgba(251, 191, 36, 0.8)",
                            color: "#ffffff",
                            borderRadius: "0.25rem",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                          }}
                        >
                          Unavailable
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ padding: "1rem", flex: 1, display: "flex", flexDirection: "column" }}>
                    <h3 style={{ fontSize: "1.1rem", fontWeight: 600, color: "#ffffff", marginBottom: "0.5rem" }}>
                      {taxi.vehicleName}
                    </h3>
                    <p style={{ fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.6)", marginBottom: "0.5rem" }}>
                      {taxi.vehicleType}
                    </p>
                    <div style={{ display: "flex", gap: "1rem", marginBottom: "0.5rem", fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.8)" }}>
                      <span>👥 {taxi.capacity?.passengers || 0} passengers</span>
                      <span>🧳 {taxi.capacity?.luggage || 0} luggage</span>
                    </div>
                    <div style={{ marginTop: "auto", paddingTop: "1rem", display: "flex", justifyContent: "flex-end" }}>
                      <button
                        type="button"
                        onClick={() => handleDeleteTaxi(taxi._id)}
                        disabled={deletingTaxi === taxi._id}
                        style={{
                          padding: "0.5rem 1rem",
                          borderRadius: "0.5rem",
                          border: "none",
                          backgroundColor: deletingTaxi === taxi._id ? "rgba(239, 68, 68, 0.5)" : "rgba(239, 68, 68, 0.8)",
                          color: "#ffffff",
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          cursor: deletingTaxi === taxi._id ? "not-allowed" : "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        {deletingTaxi === taxi._id ? (
                          <>
                            <FiLoader style={{ animation: "spin 1s linear infinite" }} />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <FiTrash2 />
                            Delete
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageTaxi;

